// src/views/admin/caisse/ADCaisseCaissesMouvements.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../../services/adminService";
import { Loader } from "../../../components/Loader";
import { useToastHook } from "../../../utils/useToast";
import { usePermissions } from "../../../context/PermissionsContext";
import { Modal, SubTabNav } from "./ADCaisseShared";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* ============================================================================
 * PERMISSIONS (module "caisse") utilisées dans ce fichier — même schéma que
 * ADEleves.jsx :
 * - caisse.configurer            : gestion des caisses (créer/modifier),
 *                                  rubriques, modes de paiement
 * - caisse.mouvementer           : encaissement / décaissement / transfert
 *                                  + consultation des mouvements d'une caisse
 * - caisse.gerer_sessions        : ouvrir / fermer une session de caisse
 * - caisse.gerer_clotures        : créer / valider une clôture de caisse
 * - caisse.affecter_utilisateurs : affecter des utilisateurs à une caisse
 *
 * Le bouton "Voir le détail" d'une caisse (qui ouvre CaisseDetail, avec ses
 * propres sous-onglets Mouvements/Clôtures/Utilisateurs) reste visible dès
 * que l'utilisateur a AU MOINS une des permissions qui s'y rattachent — le
 * détail fin est ensuite géré à l'intérieur de CaisseDetail lui-même.

 * ========================================================================== */

/* Style des cellules à texte long : retour à la ligne au lieu de la coupure
 * (ellipsis) imposée par .tbl. À étaler sur le <td> : <td style={CELL_WRAP}>.
 * Pour ajouter d'autres propriétés : style={{ ...CELL_WRAP, fontWeight: 500 }} */
const CELL_WRAP = {
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  overflow: "visible",
  textOverflow: "clip",
  minWidth: 180,
  maxWidth: 320,
};

/* Conteneur des tableaux : défilement horizontal si le tableau déborde,
 * plutôt que de masquer la fin des colonnes (overflow: hidden). */
const TABLE_CARD = { padding: 0, overflowX: "auto" };

const ActionBtn = ({ title, icon, onClick, bg, color = "white" }) => (
  <button
    title={title}
    onClick={e => { e.stopPropagation(); onClick(e); }}
    style={{ background: bg, color }}
    onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
    onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
  >
    <i className={`ti ${icon}`} />
  </button>
);

/* ============================================================================
 * CAISSES & MOUVEMENTS
 * Point d'entrée exporté par défaut, importé par ADCaisse.jsx
 * ========================================================================== */
export default function CaissesMouvements({ slug }) {
  const { canSee } = usePermissions();

  const peutConfigurer    = canSee("caisse.configurer");
  const peutMouvementer   = canSee("caisse.mouvementer");
  const peutGererSessions = canSee("caisse.gerer_sessions");
  const peutGererClotures = canSee("caisse.gerer_clotures");
  const peutAffecterUsers = canSee("caisse.affecter_utilisateurs");

  // La liste des caisses (avec drill-down vers le détail) reste utile dès
  // qu'une des permissions liées à une caisse individuelle est accordée.
  const peutVoirCaisses = peutConfigurer || peutMouvementer || peutGererSessions || peutGererClotures || peutAffecterUsers;

  const SOUS_ONGLETS_CAISSES = [
    { key: "caisses", label: "Caisses", icon: "ti-cash-register", visible: peutVoirCaisses },
    { key: "rubriques", label: "Rubriques", icon: "ti-list-details", visible: peutConfigurer },
    { key: "modes-paiement", label: "Modes de paiement", icon: "ti-credit-card", visible: peutConfigurer },
  ].filter(o => o.visible);

  const [sousOnglet, setSousOnglet] = useState(SOUS_ONGLETS_CAISSES[0]?.key || null);

  if (SOUS_ONGLETS_CAISSES.length === 0) {
    return (
      <div className="card" style={{ textAlign: "center", color: "var(--h)", padding: 40 }}>
        Vous n'avez accès à aucune section de Caisses & Mouvements.
      </div>
    );
  }

  return (
    <div>
      <SubTabNav items={SOUS_ONGLETS_CAISSES} active={sousOnglet} onChange={setSousOnglet} style={{ marginBottom: 14 }} />

      {sousOnglet === "caisses" && peutVoirCaisses && <GestionCaisses slug={slug} />}
      {sousOnglet === "rubriques" && peutConfigurer && <GestionRubriques slug={slug} />}
      {sousOnglet === "modes-paiement" && peutConfigurer && <GestionModesPaiement slug={slug} />}
    </div>
  );
}

/* ── Liste des caisses + drill-down détail ─────────────────────────────── */
const EMPTY_CAISSE = { nom: "", solde_initial: 0, date_ouverture: "", active: true };

function GestionCaisses({ slug }) {
  const { canSee } = usePermissions();
  const peutConfigurer = canSee("caisse.configurer");

  const [caisses, setCaisses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_CAISSE);
  const [caisseOuverte, setCaisseOuverte] = useState(null); // caisse dont on voit le détail

  const toast = useToastHook();
  const { getCaisses, createCaisse, updateCaisse } = useAdminService();

  useEffect(() => { if (slug) load(); }, [slug]);

  const load = async () => {
    setLoading(true);
    try {
      setCaisses(await getCaisses(slug));
    } catch (error) {
      toast.error("Impossible de charger les caisses");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setForm(EMPTY_CAISSE); setModal("create"); };
  const openEdit = (c) => { setSelected(c); setForm({ nom: c.nom, solde_initial: c.solde_initial, date_ouverture: c.date_ouverture, active: c.active }); setModal("edit"); };
  const change = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.nom.trim() || !form.date_ouverture) { toast.error("Le nom et la date d'ouverture sont requis"); return; }
    setSaving(true);
    try {
      if (modal === "create") {
        const created = await createCaisse(slug, form);
        setCaisses(prev => [...prev, created]);
        toast.success(`Caisse "${created.nom}" créée avec succès`);
      } else {
        const updated = await updateCaisse(slug, selected.id, form);
        setCaisses(prev => prev.map(c => c.id === selected.id ? updated : c));
        toast.success(`Caisse "${updated.nom}" modifiée avec succès`);
      }
      setModal(null);
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.nom?.[0] || data?.date_ouverture?.[0] || "Une erreur est survenue";
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  if (caisseOuverte) {
    return (
      <CaisseDetail
        slug={slug}
        caisse={caisseOuverte}
        toutesLesCaisses={caisses}
        onBack={() => setCaisseOuverte(null)}
      />
    );
  }

  return (
    <div>
      <div className="sb2" style={{ justifyContent: "flex-end" }}>
        <button className="bsec" onClick={load}><i className="ti ti-refresh" />Actualiser</button>
        {/* caisse.configurer */}
        {peutConfigurer && (
          <button className="bprim" onClick={openCreate}><i className="ti ti-plus" />Nouvelle caisse</button>
        )}
      </div>

      <div className="card" style={TABLE_CARD}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Solde initial</th>
              <th>Solde actuel</th>
              <th>Date d'ouverture</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {caisses.map(c => (
              <tr
                key={c.id}
                onClick={() => setCaisseOuverte(c)}
                onKeyDown={e => { if (e.key === "Enter") setCaisseOuverte(c); }}
                tabIndex={0}
                title="Cliquer pour voir le détail"
                style={{ cursor: "pointer" }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(0,0,0,0.04)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = ""; }}
              >
                <td style={{ ...CELL_WRAP, fontWeight: 500 }}>{c.nom}</td>
                <td>{Number(c.solde_initial).toLocaleString()} FCFA</td>
                <td style={{ fontWeight: 600, color: "var(--pr)" }}>
                  {Number(c.solde_actuel).toLocaleString()} FCFA
                </td>
                <td>{c.date_ouverture}</td>
                <td><span className={`pill ${c.active ? "ok" : "warn"}`}>{c.active ? "Active" : "Inactive"}</span></td>
                <td>
                  <div className="act">
                    {/* Voir détails : visible pour tous ceux qui ont accès à l'onglet Caisses */}
                    <ActionBtn title="Voir le détail" icon="ti-eye" onClick={() => setCaisseOuverte(c)} bg="var(--pr)" />
                    {/* caisse.configurer */}
                    {peutConfigurer && (
                      <ActionBtn title="Modifier" icon="ti-edit" onClick={() => openEdit(c)} bg="#ffc107" color="black" />
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {caisses.length === 0 && (
              <tr><td colSpan="6" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucune caisse trouvée</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(modal === "create" || modal === "edit") && (
        <Modal title={modal === "create" ? "Nouvelle caisse" : "Modifier la caisse"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg">
              <label>Nom *</label>
              <input type="text" placeholder="Ex: Caisse frais de scolarité" value={form.nom} onChange={e => change('nom', e.target.value)} />
            </div>
            <div className="fgrid">
              <div className="fg">
                <label>Solde initial (FCFA)</label>
                <input type="number" min="0" step="0.01" value={form.solde_initial} onChange={e => change('solde_initial', e.target.value)} />
              </div>
              <div className="fg">
                <label>Date d'ouverture *</label>
                <input type="date" value={form.date_ouverture} onChange={e => change('date_ouverture', e.target.value)} />
              </div>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
              <input type="checkbox" checked={form.active} onChange={e => change('active', e.target.checked)} />
              Active
            </label>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modal === "create" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Détail d'une caisse : solde, mouvements, sessions, clôtures, utilisateurs ── */
function CaisseDetail({ slug, caisse, toutesLesCaisses, onBack }) {
  const { canSee } = usePermissions();
  const peutMouvementer   = canSee("caisse.mouvementer");
  const peutGererSessions = canSee("caisse.gerer_sessions");
  const peutGererClotures = canSee("caisse.gerer_clotures");
  const peutAffecterUsers = canSee("caisse.affecter_utilisateurs");

  const SOUS_ONGLETS_DETAIL = [
    { key: "mouvements", label: "Mouvements", icon: "ti-list-numbers", visible: peutMouvementer },
    { key: "clotures", label: "Clôtures", icon: "ti-lock", visible: peutGererClotures },
    { key: "utilisateurs", label: "Utilisateurs affectés", icon: "ti-users", visible: peutAffecterUsers },
  ].filter(o => o.visible);

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sousOnglet, setSousOnglet] = useState(SOUS_ONGLETS_DETAIL[0]?.key || null);
  const [modal, setModal] = useState(null); // 'encaissement' | 'decaissement' | 'transfert' | 'ouvrir-session' | 'fermer-session'

  const toast = useToastHook();
  const { getCaisseDetail, ouvrirSessionCaisse, fermerSessionCaisse } = useAdminService();

  useEffect(() => { if (slug && caisse) loadDetail(); }, [slug, caisse]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      setDetail(await getCaisseDetail(slug, caisse.id));
    } catch (error) {
      toast.error("Impossible de charger le détail de la caisse");
    } finally {
      setLoading(false);
    }
  };

  const handleOuvrirSession = async (soldeOuverture) => {
    try {
      await ouvrirSessionCaisse(slug, caisse.id, soldeOuverture || null);
      toast.success("Session de caisse ouverte");
      setModal(null);
      loadDetail(); // recharge session_ouverte depuis le backend
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible d'ouvrir une session pour cette caisse");
    }
  };

  const handleFermerSession = async (soldeFermeture) => {
    try {
      await fermerSessionCaisse(slug, caisse.id, soldeFermeture || null);
      toast.success("Session de caisse fermée");
      setModal(null);
      loadDetail();
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de fermer la session");
    }
  };

  if (loading || !detail) return <Loader />;

  // Vient directement du backend (CaisseDetailAPIView) — persiste au F5,
  // peu importe quel utilisateur affecté a ouvert la session.
  const sessionOuverte = detail.session_ouverte;

  return (
    <div>
      <div className="sb2">
        <button className="bsec" onClick={onBack}><i className="ti ti-arrow-left" />Retour aux caisses</button>
      </div>

      <div className="card" style={{ marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--t)" }}>{detail.nom}</div>
          <div style={{ fontSize: 13, color: "var(--m)" }}>Solde actuel</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: "var(--pr)" }}>{Number(detail.solde_actuel).toLocaleString()} FCFA</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {/* caisse.mouvementer */}
          {peutMouvementer && (
            <>
              <button className="bsec" onClick={() => setModal("encaissement")}><i className="ti ti-cash" />Encaissement</button>
              <button className="bsec rd" onClick={() => setModal("decaissement")}><i className="ti ti-cash-banknote" />Décaissement</button>
              <button className="bsec" onClick={() => setModal("transfert")}><i className="ti ti-transfer" />Transfert</button>
            </>
          )}
          {/* caisse.gerer_sessions */}
          {peutGererSessions && (
            sessionOuverte ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "var(--h)" }}>Ouverte par {sessionOuverte.utilisateur_username}</span>
                <button className="bprim rd" onClick={() => setModal("fermer-session")}><i className="ti ti-lock" />Fermer la session</button>
              </div>
            ) : (
              <button className="bprim" onClick={() => setModal("ouvrir-session")}><i className="ti ti-lock-open" />Ouvrir une session</button>
            )
          )}
        </div>
      </div>

      {SOUS_ONGLETS_DETAIL.length > 0 ? (
        <>
          <SubTabNav items={SOUS_ONGLETS_DETAIL} active={sousOnglet} onChange={setSousOnglet} style={{ marginBottom: 14 }} />

          {/* caisse.mouvementer */}
          {sousOnglet === "mouvements" && peutMouvementer && (
            <MouvementsCaisse slug={slug} caisseId={caisse.id} caisseNom={detail.nom} onMouvementCree={loadDetail} />
          )}
          {/* caisse.gerer_clotures */}
          {sousOnglet === "clotures" && peutGererClotures && <CloturesCaisse slug={slug} caisseId={caisse.id} />}
          {/* caisse.affecter_utilisateurs */}
          {sousOnglet === "utilisateurs" && peutAffecterUsers && <UtilisateursCaisse slug={slug} caisseId={caisse.id} />}
        </>
      ) : (
        <div className="card" style={{ textAlign: "center", color: "var(--h)", padding: 24 }}>
          Vous n'avez accès à aucun sous-onglet de détail pour cette caisse.
        </div>
      )}

      {modal === "encaissement" && peutMouvementer && (
        <EncaissementModal slug={slug} caisseId={caisse.id} onClose={() => setModal(null)} onCreated={() => { setModal(null); loadDetail(); }} />
      )}
      {modal === "decaissement" && peutMouvementer && (
        <DecaissementModal slug={slug} caisseId={caisse.id} onClose={() => setModal(null)} onCreated={() => { setModal(null); loadDetail(); }} />
      )}
      {modal === "transfert" && peutMouvementer && (
        <TransfertModal slug={slug} caisses={toutesLesCaisses} caisseSourceInitiale={caisse.id} onClose={() => setModal(null)} onCreated={() => { setModal(null); loadDetail(); }} />
      )}
      {modal === "ouvrir-session" && peutGererSessions && (
        <SessionModal titre="Ouvrir une session de caisse" label="Solde d'ouverture compté (FCFA)" defaut={detail.solde_actuel} onClose={() => setModal(null)} onConfirm={handleOuvrirSession} />
      )}
      {modal === "fermer-session" && peutGererSessions && (
        <SessionModal titre="Fermer la session de caisse" label="Solde de fermeture compté (FCFA)" defaut={detail.solde_actuel} onClose={() => setModal(null)} onConfirm={handleFermerSession} />
      )}
    </div>
  );
}

function SessionModal({ titre, label, defaut, onClose, onConfirm }) {
  const [valeur, setValeur] = useState(defaut ?? "");
  return (
    <Modal title={titre} onClose={onClose}>
      <div className="fg">
        <label>{label}</label>
        <input type="number" step="0.01" value={valeur} onChange={e => setValeur(e.target.value)} />
      </div>
      <div className="factions">
        <button className="bsec" onClick={onClose}>Annuler</button>
        <button className="bprim" onClick={() => onConfirm(valeur === "" ? null : valeur)}><i className="ti ti-check" /> Confirmer</button>
      </div>
    </Modal>
  );
}

/* ── Mouvements ─────────────────────────────────────────────── */
const TYPE_MVT_LABELS = {
  ENCAISSEMENT: { label: "Encaissement", pill: "ok" },
  DECAISSEMENT: { label: "Décaissement", pill: "err" },
  TRANSFERT_ENTREE: { label: "Transfert (entrée)", pill: "info" },
  TRANSFERT_SORTIE: { label: "Transfert (sortie)", pill: "warn" },
};

function MouvementsCaisse({ slug, caisseId, caisseNom }) {
  const [mouvements, setMouvements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [generatingPDF, setGeneratingPDF] = useState(false);

  const toast = useToastHook();
  const { getMouvementsCaisse } = useAdminService();

  useEffect(() => { if (slug && caisseId) load(); }, [slug, caisseId, dateDebut, dateFin]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;
      setMouvements(await getMouvementsCaisse(slug, caisseId, params));
    } catch (error) {
      toast.error("Impossible de charger les mouvements");
    } finally {
      setLoading(false);
    }
  };

  const periodeLabel = (dateDebut || dateFin)
    ? `Période du ${dateDebut || "…"} au ${dateFin || "…"}`
    : "Depuis toujours";

  // Totaux de la période affichée, mouvements validés uniquement — un
  // mouvement annulé ne doit pas gonfler le total encaissé/décaissé.
  const mouvementsValides = mouvements.filter(m => m.statut === "VALIDE");
  const totalEncaisse = mouvementsValides
    .filter(m => m.type_mouvement === "ENCAISSEMENT" || m.type_mouvement === "TRANSFERT_ENTREE")
    .reduce((s, m) => s + Number(m.montant), 0);
  const totalDecaisse = mouvementsValides
    .filter(m => m.type_mouvement === "DECAISSEMENT" || m.type_mouvement === "TRANSFERT_SORTIE")
    .reduce((s, m) => s + Number(m.montant), 0);
  const soldeNet = totalEncaisse - totalDecaisse;

  // ─── PDF (jsPDF + autoTable — même pattern que ADCaisseBilan.jsx) ──────
  const genererPDFMouvements = () => {
    if (mouvements.length === 0) return;
    setGeneratingPDF(true);
    try {
      const doc = new jsPDF("portrait", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const dateGen = new Date().toLocaleDateString("fr-FR");

      // Number(x).toLocaleString() insère une espace fine insécable (U+202F)
      // comme séparateur de milliers, non supportée par l'encodage des
      // polices standard de jsPDF (elle s'affiche alors comme un "/"). On
      // formate donc les montants nous-mêmes avec une espace normale.
      const formatMontant = (n) => {
        const val = Math.round(Number(n) || 0);
        const neg = val < 0;
        const digits = Math.abs(val).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
        return neg ? `-${digits}` : digits;
      };

      doc.setFontSize(16);
      doc.setTextColor(0, 64, 128);
      doc.setFont(undefined, "bold");
      doc.text("MOUVEMENTS DE CAISSE", pageWidth / 2, 18, { align: "center" });

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, "normal");
      doc.text(`Caisse : ${caisseNom || "—"}`, pageWidth / 2, 27, { align: "center" });

      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(periodeLabel, pageWidth / 2, 34, { align: "center" });

      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(`Généré le ${dateGen}`, pageWidth / 2, 40, { align: "center" });

      doc.setDrawColor(0, 64, 128);
      doc.setLineWidth(0.5);
      doc.line(14, 43, pageWidth - 14, 43);

      let y = 50;
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Total encaissé : ${formatMontant(totalEncaisse)} FCFA`, 14, y); y += 5.5;
      doc.text(`Total décaissé : ${formatMontant(totalDecaisse)} FCFA`, 14, y); y += 5.5;
      doc.text(`Solde net : ${formatMontant(soldeNet)} FCFA`, 14, y); y += 8;

      autoTable(doc, {
        startY: y,
        head: [["Date", "Type", "Rubrique", "Libellé", "Montant", "Par", "Statut"]],
        body: mouvements.map(m => {
          const t = TYPE_MVT_LABELS[m.type_mouvement] || { label: m.type_mouvement };
          const sortant = m.type_mouvement === "DECAISSEMENT" || m.type_mouvement === "TRANSFERT_SORTIE";
          return [
            m.date_comptable,
            t.label,
            m.rubrique_libelle || "—",
            m.libelle || "—",
            `${sortant ? "-" : "+"}${formatMontant(m.montant)} FCFA`,
            m.utilisateur_username || "—",
            m.statut,
          ];
        }),
        columnStyles: {
          0: { halign: "center" },
          4: { halign: "right" },
          6: { halign: "center" },
        },
        theme: "grid",
        styles: { fontSize: 8.5, cellPadding: 3, textColor: [0, 0, 0] },
        headStyles: { fillColor: [0, 64, 128], textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
        alternateRowStyles: { fillColor: [240, 246, 255] },
        margin: { left: 14, right: 14, top: 18, bottom: 20 },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} / ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: "center" });
      }

      const suffixe = (caisseNom || "caisse").replace(/\s+/g, "-");
      doc.save(`mouvements-${suffixe}-${dateGen.replace(/\//g, "-")}.pdf`);
      toast.success("PDF généré avec succès");
    } catch (error) {
      toast.error("Impossible de générer le PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  // ─── Impression (fenêtre HTML dédiée) ──────────────────────────────────
  const imprimerMouvements = () => {
    if (mouvements.length === 0) return;
    const dateGen = new Date().toLocaleDateString("fr-FR");

    const td = (v, align = "left") =>
      `<td style="padding:6px 8px;border:1px solid #ccc;text-align:${align};">${v}</td>`;

    const lignes = mouvements.map((m, i) => {
      const t = TYPE_MVT_LABELS[m.type_mouvement] || { label: m.type_mouvement };
      const sortant = m.type_mouvement === "DECAISSEMENT" || m.type_mouvement === "TRANSFERT_SORTIE";
      return `
        <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f0f6ff"}">
          ${td(m.date_comptable, "center")}
          ${td(t.label)}
          ${td(m.rubrique_libelle || "—")}
          ${td(m.libelle || "—")}
          ${td(`${sortant ? "-" : "+"}${Number(m.montant).toLocaleString()} FCFA`, "right")}
          ${td(m.utilisateur_username || "—")}
          ${td(m.statut, "center")}
        </tr>`;
    }).join("");

    const html = `
      <!DOCTYPE html>
      <html>
        <head><meta charset="UTF-8"><title>Mouvements de caisse${caisseNom ? " - " + caisseNom : ""}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Times New Roman', serif; padding: 20px; background: white; color: #000; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            .header { text-align: center; margin-bottom: 18px; border-bottom: 2px solid #004080; padding-bottom: 12px; }
            .header h1 { font-size: 20pt; color: #004080; text-transform: uppercase; margin-bottom: 6px; }
            .header .sous-titre { font-size: 13pt; font-weight: bold; color: #222; margin-bottom: 4px; }
            .header .meta { font-size: 10pt; color: #555; }
            .kpis { font-size: 10.5pt; margin-bottom: 12px; }
            .kpis div { padding: 2px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 9.5pt; }
            thead tr { background: #004080 !important; color: white; }
            thead th { padding: 7px 8px; border: 1px solid #003366; text-align: center; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Mouvements de caisse</h1>
            <div class="sous-titre">Caisse : ${caisseNom || "—"}</div>
            <div class="meta">${periodeLabel} &nbsp;·&nbsp; Généré le ${dateGen}</div>
          </div>
          <div class="kpis">
            <div>Total encaissé : <strong>${totalEncaisse.toLocaleString()} FCFA</strong></div>
            <div>Total décaissé : <strong>${totalDecaisse.toLocaleString()} FCFA</strong></div>
            <div>Solde net : <strong>${soldeNet.toLocaleString()} FCFA</strong></div>
          </div>
          <table>
            <thead><tr><th>Date</th><th>Type</th><th>Rubrique</th><th>Libellé</th><th>Montant</th><th>Par</th><th>Statut</th></tr></thead>
            <tbody>${lignes}</tbody>
          </table>
          <script>
            window.onload = function() { setTimeout(function() { window.print(); }, 300); };
            window.onafterprint = function() { window.close(); };
          <\/script>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank", "width=900,height=700,scrollbars=yes");
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2">
        <div className="fg" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <label style={{ margin: 0 }}>Du</label>
          <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} />
        </div>
        <div className="fg" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <label style={{ margin: 0 }}>Au</label>
          <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} />
        </div>
        <button className="bsec" onClick={load}><i className="ti ti-refresh" />Actualiser</button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            className="bsec"
            onClick={genererPDFMouvements}
            disabled={mouvements.length === 0 || generatingPDF}
            style={{ color: "#dc3545", borderColor: "#dc3545" }}
          >
            <i className="ti ti-file-pdf" /> {generatingPDF ? "Génération..." : "Télécharger PDF"}
          </button>
          <button
            className="bsec"
            onClick={imprimerMouvements}
            disabled={mouvements.length === 0}
            style={{ color: "#6f42c1", borderColor: "#6f42c1" }}
          >
            <i className="ti ti-printer" />Imprimer
          </button>
        </div>
      </div>

      <div className="card" style={TABLE_CARD}>
        <table className="tbl">
          <thead>
            <tr><th>Date</th><th>Type</th><th>Rubrique</th><th>Libellé</th><th>Montant</th><th>Par</th><th>Statut</th></tr>
          </thead>
          <tbody>
            {mouvements.map(m => {
              const t = TYPE_MVT_LABELS[m.type_mouvement] || { label: m.type_mouvement, pill: "neu" };
              const sortant = m.type_mouvement === "DECAISSEMENT" || m.type_mouvement === "TRANSFERT_SORTIE";
              return (
                <tr key={m.id}>
                  <td>{m.date_comptable}</td>
                  <td><span className={`pill ${t.pill}`}>{t.label}</span></td>
                  <td style={CELL_WRAP}>{m.rubrique_libelle}</td>
                  <td style={CELL_WRAP}>{m.libelle}</td>
                  <td style={{ color: sortant ? "var(--rd)" : "var(--gn)", fontWeight: 600 }}>
                    {sortant ? "-" : "+"}{Number(m.montant).toLocaleString()} FCFA
                  </td>
                  <td>{m.utilisateur_username}</td>
                  <td><span className={`pill ${m.statut === "VALIDE" ? "ok" : "err"}`}>{m.statut}</span></td>
                </tr>
              );
            })}
            {mouvements.length === 0 && (
              <tr><td colSpan="7" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucun mouvement trouvé</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Encaissement (recette libre, hors suivi élève) ────────────────────── */
function EncaissementModal({ slug, caisseId, onClose, onCreated }) {
  const [rubriques, setRubriques] = useState([]);
  const [rubrique, setRubrique] = useState("");
  const [montant, setMontant] = useState("");
  const [libelle, setLibelle] = useState("");
  const [dateComptable, setDateComptable] = useState("");
  const [fichier, setFichier] = useState(null);
  const [saving, setSaving] = useState(false);

  const toast = useToastHook();
  const { getRubriques, creerEncaissement } = useAdminService();

  useEffect(() => {
    getRubriques(slug, { type_mouvement: "RECETTE" }).then(setRubriques).catch(() => toast.error("Impossible de charger les rubriques"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleSave = async () => {
    if (!rubrique || !montant || !libelle.trim()) { toast.error("La rubrique, le montant et le libellé sont requis"); return; }
    setSaving(true);
    try {
      const data = { rubrique, montant, libelle };
      if (dateComptable) data.date_comptable = dateComptable;
      await creerEncaissement(slug, caisseId, data, fichier);
      toast.success("Encaissement enregistré avec succès");
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible d'enregistrer cet encaissement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Nouvel encaissement" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div className="fg">
          <label>Rubrique (recette) *</label>
          <select value={rubrique} onChange={e => setRubrique(e.target.value)}>
            <option value="">-- Sélectionner --</option>
            {rubriques.map(r => <option key={r.id} value={r.id}>{r.libelle}</option>)}
          </select>
        </div>
        <div className="fgrid">
          <div className="fg">
            <label>Montant (FCFA) *</label>
            <input type="number" min="0" step="0.01" value={montant} onChange={e => setMontant(e.target.value)} />
          </div>
          <div className="fg">
            <label>Date comptable</label>
            <input type="date" value={dateComptable} onChange={e => setDateComptable(e.target.value)} />
          </div>
        </div>
        <div className="fg">
          <label>Libellé *</label>
          <textarea value={libelle} onChange={e => setLibelle(e.target.value)} />
        </div>
        <div className="fg">
          <label>Pièce justificative</label>
          <input type="file" onChange={e => setFichier(e.target.files?.[0] || null)} />
        </div>
      </div>
      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={saving}>Annuler</button>
        <button className="bprim" onClick={handleSave} disabled={saving}>{saving ? "En cours..." : <><i className="ti ti-check" /> Encaisser</>}</button>
      </div>
    </Modal>
  );
}

function DecaissementModal({ slug, caisseId, onClose, onCreated }) {
  const [rubriques, setRubriques] = useState([]);
  const [rubrique, setRubrique] = useState("");
  const [montant, setMontant] = useState("");
  const [libelle, setLibelle] = useState("");
  const [dateComptable, setDateComptable] = useState("");
  const [fichier, setFichier] = useState(null);
  const [saving, setSaving] = useState(false);

  const toast = useToastHook();
  const { getRubriques, creerDecaissement } = useAdminService();

  useEffect(() => {
    getRubriques(slug, { type_mouvement: "DEPENSE" }).then(setRubriques).catch(() => toast.error("Impossible de charger les rubriques"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleSave = async () => {
    if (!rubrique || !montant || !libelle.trim()) { toast.error("La rubrique, le montant et le libellé sont requis"); return; }
    setSaving(true);
    try {
      const data = { rubrique, montant, libelle };
      if (dateComptable) data.date_comptable = dateComptable;
      await creerDecaissement(slug, caisseId, data, fichier);
      toast.success("Décaissement enregistré avec succès");
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible d'enregistrer ce décaissement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Nouveau décaissement" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div className="fg">
          <label>Rubrique (dépense) *</label>
          <select value={rubrique} onChange={e => setRubrique(e.target.value)}>
            <option value="">-- Sélectionner --</option>
            {rubriques.map(r => <option key={r.id} value={r.id}>{r.libelle}</option>)}
          </select>
        </div>
        <div className="fgrid">
          <div className="fg">
            <label>Montant (FCFA) *</label>
            <input type="number" min="0" step="0.01" value={montant} onChange={e => setMontant(e.target.value)} />
          </div>
          <div className="fg">
            <label>Date comptable</label>
            <input type="date" value={dateComptable} onChange={e => setDateComptable(e.target.value)} />
          </div>
        </div>
        <div className="fg">
          <label>Libellé *</label>
          <textarea value={libelle} onChange={e => setLibelle(e.target.value)} />
        </div>
        <div className="fg">
          <label>Pièce justificative</label>
          <input type="file" onChange={e => setFichier(e.target.files?.[0] || null)} />
        </div>
      </div>
      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={saving}>Annuler</button>
        <button className="bprim rd" onClick={handleSave} disabled={saving}>{saving ? "En cours..." : <><i className="ti ti-check" /> Décaisser</>}</button>
      </div>
    </Modal>
  );
}

function TransfertModal({ slug, caisses, caisseSourceInitiale, onClose, onCreated }) {
  const [rubriques, setRubriques] = useState([]);
  const [caisseSource, setCaisseSource] = useState(caisseSourceInitiale || "");
  const [caisseDestination, setCaisseDestination] = useState("");
  const [montant, setMontant] = useState("");
  const [libelle, setLibelle] = useState("");
  const [rubriqueSortie, setRubriqueSortie] = useState("");
  const [rubriqueEntree, setRubriqueEntree] = useState("");
  const [saving, setSaving] = useState(false);

  const toast = useToastHook();
  const { getRubriques, creerTransfert } = useAdminService();

  useEffect(() => {
    getRubriques(slug).then(setRubriques).catch(() => toast.error("Impossible de charger les rubriques"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleSave = async () => {
    if (!caisseSource || !caisseDestination || caisseSource === caisseDestination) { toast.error("Choisissez deux caisses différentes"); return; }
    if (!montant || !libelle.trim() || !rubriqueSortie || !rubriqueEntree) { toast.error("Tous les champs sont requis"); return; }
    setSaving(true);
    try {
      await creerTransfert(slug, {
        caisse_source: caisseSource, caisse_destination: caisseDestination,
        montant, libelle, rubrique_sortie: rubriqueSortie, rubrique_entree: rubriqueEntree,
      });
      toast.success("Transfert effectué avec succès");
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible d'effectuer ce transfert");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Nouveau transfert entre caisses" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div className="fgrid">
          <div className="fg">
            <label>Caisse source *</label>
            <select value={caisseSource} onChange={e => setCaisseSource(e.target.value)}>
              <option value="">-- Sélectionner --</option>
              {caisses.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>
          <div className="fg">
            <label>Caisse destination *</label>
            <select value={caisseDestination} onChange={e => setCaisseDestination(e.target.value)}>
              <option value="">-- Sélectionner --</option>
              {caisses.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>
        </div>
        <div className="fg">
          <label>Montant (FCFA) *</label>
          <input type="number" min="0.01" step="0.01" value={montant} onChange={e => setMontant(e.target.value)} />
        </div>
        <div className="fgrid">
          <div className="fg">
            <label>Rubrique de sortie *</label>
            <select value={rubriqueSortie} onChange={e => setRubriqueSortie(e.target.value)}>
              <option value="">-- Sélectionner --</option>
              {rubriques.map(r => <option key={r.id} value={r.id}>{r.libelle}</option>)}
            </select>
          </div>
          <div className="fg">
            <label>Rubrique d'entrée *</label>
            <select value={rubriqueEntree} onChange={e => setRubriqueEntree(e.target.value)}>
              <option value="">-- Sélectionner --</option>
              {rubriques.map(r => <option key={r.id} value={r.id}>{r.libelle}</option>)}
            </select>
          </div>
        </div>
        <div className="fg">
          <label>Libellé *</label>
          <textarea value={libelle} onChange={e => setLibelle(e.target.value)} />
        </div>
      </div>
      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={saving}>Annuler</button>
        <button className="bprim" onClick={handleSave} disabled={saving}>{saving ? "En cours..." : <><i className="ti ti-check" /> Transférer</>}</button>
      </div>
    </Modal>
  );
}

/* ── Clôtures ───────────────────────────────────────────────── */
function CloturesCaisse({ slug, caisseId }) {
  const [clotures, setClotures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const [dateCloture, setDateCloture] = useState("");
  const [soldeCompte, setSoldeCompte] = useState("");
  const [observations, setObservations] = useState("");

  const toast = useToastHook();
  const { getCloturesCaisse, creerClotureCaisse, validerClotureCaisse } = useAdminService();

  useEffect(() => { if (slug && caisseId) load(); }, [slug, caisseId]);

  const load = async () => {
    setLoading(true);
    try {
      setClotures(await getCloturesCaisse(slug, caisseId));
    } catch (error) {
      toast.error("Impossible de charger les clôtures");
    } finally {
      setLoading(false);
    }
  };

  const handleCreer = async () => {
    if (!dateCloture || soldeCompte === "") { toast.error("La date et le solde compté sont requis"); return; }
    setSaving(true);
    try {
      const cloture = await creerClotureCaisse(slug, caisseId, { date_cloture: dateCloture, solde_compte: soldeCompte, observations });
      toast.success(
        Number(cloture.ecart) === 0
          ? "Clôture créée — aucun écart"
          : `Clôture créée — écart de ${Number(cloture.ecart).toLocaleString()} FCFA`
      );
      setModal(null);
      setDateCloture(""); setSoldeCompte(""); setObservations("");
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de créer cette clôture");
    } finally {
      setSaving(false);
    }
  };

  const handleValider = async (c) => {
    try {
      await validerClotureCaisse(slug, c.id);
      toast.success("Clôture validée avec succès");
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de valider cette clôture");
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2" style={{ justifyContent: "flex-end" }}>
        <button className="bprim" onClick={() => setModal("create")}><i className="ti ti-plus" />Nouvelle clôture</button>
      </div>

      <div className="card" style={TABLE_CARD}>
        <table className="tbl">
          <thead>
            <tr><th>Date</th><th>Solde théorique</th><th>Solde compté</th><th>Écart</th><th>Statut</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {clotures.map(c => (
              <tr key={c.id}>
                <td>{c.date_cloture}</td>
                <td>{Number(c.solde_theorique).toLocaleString()} FCFA</td>
                <td>{Number(c.solde_compte).toLocaleString()} FCFA</td>
                <td style={{ color: Number(c.ecart) === 0 ? "var(--gn)" : "var(--rd)", fontWeight: 600 }}>
                  {Number(c.ecart).toLocaleString()} FCFA
                </td>
                <td><span className={`pill ${c.statut === "VALIDE" ? "ok" : c.statut === "CLOTURE" ? "warn" : "neu"}`}>{c.statut}</span></td>
                <td>
                  {c.statut !== "VALIDE" && (
                    <div className="act">
                      <ActionBtn title="Valider" icon="ti-check" onClick={() => handleValider(c)} bg="var(--gn)" />
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {clotures.length === 0 && (
              <tr><td colSpan="6" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucune clôture trouvée</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal === "create" && (
        <Modal title="Nouvelle clôture de caisse" onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg">
              <label>Date de clôture *</label>
              <input type="date" value={dateCloture} onChange={e => setDateCloture(e.target.value)} />
            </div>
            <div className="fg">
              <label>Solde compté (FCFA) *</label>
              <input type="number" step="0.01" value={soldeCompte} onChange={e => setSoldeCompte(e.target.value)} />
            </div>
            <div className="fg">
              <label>Observations</label>
              <textarea value={observations} onChange={e => setObservations(e.target.value)} />
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleCreer} disabled={saving}>{saving ? "En cours..." : <><i className="ti ti-check" /> Créer la clôture</>}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Utilisateurs affectés à une caisse ────────────────────────── */
function UtilisateursCaisse({ slug, caisseId }) {
  const [affectations, setAffectations] = useState([]);
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const [utilisateurChoisi, setUtilisateurChoisi] = useState("");

  const toast = useToastHook();
  const { getCaisseUtilisateurs, affecterUtilisateurCaisse, getUtilisateurs } = useAdminService();

  useEffect(() => { if (slug && caisseId) load(); }, [slug, caisseId]);

  const load = async () => {
    setLoading(true);
    try {
      const [aff, users] = await Promise.all([
        getCaisseUtilisateurs(slug, caisseId),
        getUtilisateurs(slug, { page_size: "all" }),
      ]);
      setAffectations(aff);
      setUtilisateurs(users.results || users);
    } catch (error) {
      toast.error("Impossible de charger les utilisateurs affectés");
    } finally {
      setLoading(false);
    }
  };

  const disponibles = utilisateurs.filter(u => !affectations.some(a => a.utilisateur === u.id));

  const handleAffecter = async () => {
    if (!utilisateurChoisi) { toast.error("Choisissez un utilisateur"); return; }
    setSaving(true);
    try {
      const created = await affecterUtilisateurCaisse(slug, caisseId, { utilisateur: utilisateurChoisi });
      setAffectations(prev => [...prev, created]);
      toast.success("Utilisateur affecté avec succès");
      setModal(null);
      setUtilisateurChoisi("");
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible d'affecter cet utilisateur");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2" style={{ justifyContent: "flex-end" }}>
        <button className="bprim" onClick={() => setModal("create")} disabled={disponibles.length === 0}>
          <i className="ti ti-user-plus" />Affecter un utilisateur
        </button>
      </div>

      <div className="card" style={TABLE_CARD}>
        <table className="tbl">
          <thead><tr><th>Utilisateur</th><th>Date d'affectation</th></tr></thead>
          <tbody>
            {affectations.map(a => (
              <tr key={a.id}>
                <td style={{ ...CELL_WRAP, fontWeight: 500 }}>{a.utilisateur_username}</td>
                <td>{a.date_affectation}</td>
              </tr>
            ))}
            {affectations.length === 0 && (
              <tr><td colSpan="2" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucun utilisateur affecté</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal === "create" && (
        <Modal title="Affecter un utilisateur à cette caisse" onClose={() => setModal(null)}>
          <div className="fg">
            <label>Utilisateur *</label>
            <select value={utilisateurChoisi} onChange={e => setUtilisateurChoisi(e.target.value)}>
              <option value="">-- Sélectionner --</option>
              {disponibles.map(u => <option key={u.id} value={u.id}>{u.username}{u.first_name ? ` (${u.first_name} ${u.last_name})` : ""}</option>)}
            </select>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleAffecter} disabled={saving}>{saving ? "..." : <><i className="ti ti-check" /> Affecter</>}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Rubriques ──────────────────────────────────────────────── */
const EMPTY_RUB = { code: "", libelle: "", type_mouvement: "RECETTE", caisse_defaut: "" };

function GestionRubriques({ slug }) {
  const { canSee } = usePermissions();
  const peutConfigurer = canSee("caisse.configurer");

  const [items, setItems] = useState([]);
  const [caisses, setCaisses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filtreType, setFiltreType] = useState("");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_RUB);

  const toast = useToastHook();
  const { getRubriques, createRubrique, updateRubrique, deleteRubrique, getCaisses } = useAdminService();

  useEffect(() => { if (slug) init(); }, [slug]);
  useEffect(() => { if (slug) load(); }, [slug, filtreType]);

  const init = async () => {
    try { setCaisses(await getCaisses(slug)); } catch (error) { /* silencieux, non bloquant */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtreType) params.type_mouvement = filtreType;
      setItems(await getRubriques(slug, params));
    } catch (error) {
      toast.error("Impossible de charger les rubriques");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setForm(EMPTY_RUB); setModal("create"); };
  const openEdit = (r) => { setSelected(r); setForm({ code: r.code, libelle: r.libelle, type_mouvement: r.type_mouvement, caisse_defaut: r.caisse_defaut || "" }); setModal("edit"); };
  const openDelete = (r) => { setSelected(r); setModal("delete"); };
  const change = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.code.trim() || !form.libelle.trim()) { toast.error("Le code et le libellé sont requis"); return; }
    setSaving(true);
    try {
      const data = { ...form, caisse_defaut: form.caisse_defaut || null };
      if (modal === "create") {
        const created = await createRubrique(slug, data);
        setItems(prev => [...prev, created]);
        toast.success(`Rubrique "${created.libelle}" créée avec succès`);
      } else {
        const updated = await updateRubrique(slug, selected.id, data);
        setItems(prev => prev.map(r => r.id === selected.id ? updated : r));
        toast.success(`Rubrique "${updated.libelle}" modifiée avec succès`);
      }
      setModal(null);
      setForm(EMPTY_RUB);
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.code?.[0] || data?.libelle?.[0] || "Une erreur est survenue";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteRubrique(slug, selected.id);
      setItems(prev => prev.filter(r => r.id !== selected.id));
      toast.success(`Rubrique "${selected.libelle}" supprimée avec succès`);
      setModal(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de supprimer cette rubrique — elle est déjà utilisée dans des mouvements");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2">
        <select value={filtreType} onChange={e => setFiltreType(e.target.value)}>
          <option value="">-- Tous les types --</option>
          <option value="RECETTE">Recette</option>
          <option value="DEPENSE">Dépense</option>
        </select>
        <button className="bsec" onClick={load}><i className="ti ti-refresh" />Actualiser</button>
        {/* caisse.configurer */}
        {peutConfigurer && (
          <button className="bprim" onClick={openCreate}><i className="ti ti-plus" />Nouvelle rubrique</button>
        )}
      </div>

      <div className="card" style={TABLE_CARD}>
        <table className="tbl">
          <thead><tr><th>Code</th><th>Libellé</th><th>Type</th><th>Caisse par défaut</th><th>Actions</th></tr></thead>
          <tbody>
            {items.map(r => (
              <tr key={r.id}>
                <td style={{ fontWeight: 500 }}>{r.code}</td>
                <td style={CELL_WRAP}>{r.libelle}</td>
                <td><span className={`pill ${r.type_mouvement === "RECETTE" ? "ok" : "err"}`}>{r.type_mouvement === "RECETTE" ? "Recette" : "Dépense"}</span></td>
                <td style={CELL_WRAP}>{caisses.find(c => c.id === r.caisse_defaut)?.nom || "—"}</td>
                <td>
                  {/* caisse.configurer */}
                  {peutConfigurer && (
                    <div className="act">
                      <ActionBtn title="Modifier" icon="ti-edit" onClick={() => openEdit(r)} bg="#ffc107" color="black" />
                      <ActionBtn title="Supprimer" icon="ti-trash" onClick={() => openDelete(r)} bg="var(--rd)" />
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan="5" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucune rubrique trouvée</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(modal === "create" || modal === "edit") && (
        <Modal title={modal === "create" ? "Nouvelle rubrique" : "Modifier la rubrique"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fgrid">
              <div className="fg">
                <label>Code *</label>
                <input type="text" value={form.code} onChange={e => change('code', e.target.value.toUpperCase())} />
              </div>
              <div className="fg">
                <label>Type *</label>
                <select value={form.type_mouvement} onChange={e => change('type_mouvement', e.target.value)}>
                  <option value="RECETTE">Recette</option>
                  <option value="DEPENSE">Dépense</option>
                </select>
              </div>
            </div>
            <div className="fg">
              <label>Libellé *</label>
              <input type="text" placeholder="Ex: Loyer, Livre de lecture..." value={form.libelle} onChange={e => change('libelle', e.target.value)} />
            </div>
            <div className="fg">
              <label>Caisse par défaut</label>
              <select value={form.caisse_defaut} onChange={e => change('caisse_defaut', e.target.value)}>
                <option value="">-- Aucune --</option>
                {caisses.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>{saving ? "..." : <><i className="ti ti-check" /> {modal === "create" ? "Créer" : "Enregistrer"}</>}</button>
          </div>
        </Modal>
      )}

      {modal === "delete" && selected && (
        <Modal title="Supprimer la rubrique" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>Supprimer <strong>{selected.libelle}</strong> ?</div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>{saving ? "..." : "Supprimer"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Modes de paiement ──────────────────────────────────────── */
function GestionModesPaiement({ slug }) {
  const { canSee } = usePermissions();
  const peutConfigurer = canSee("caisse.configurer");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [libelle, setLibelle] = useState("");
  const [actif, setActif] = useState(true);

  const toast = useToastHook();
  const { getModesPaiement, createModePaiement, updateModePaiement, deleteModePaiement } = useAdminService();

  useEffect(() => { if (slug) load(); }, [slug]);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await getModesPaiement(slug));
    } catch (error) {
      toast.error("Impossible de charger les modes de paiement");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setLibelle(""); setActif(true); setModal("create"); };
  const openEdit = (m) => { setSelected(m); setLibelle(m.libelle); setActif(m.actif); setModal("edit"); };
  const openDelete = (m) => { setSelected(m); setModal("delete"); };

  const handleSave = async () => {
    if (!libelle.trim()) { toast.error("Le libellé est requis"); return; }
    setSaving(true);
    try {
      if (modal === "create") {
        const created = await createModePaiement(slug, { libelle, actif: true });
        setItems(prev => [...prev, created]);
        toast.success(`Mode de paiement "${created.libelle}" créé avec succès`);
      } else {
        const updated = await updateModePaiement(slug, selected.id, { libelle, actif });
        setItems(prev => prev.map(m => m.id === selected.id ? updated : m));
        toast.success(`Mode de paiement "${updated.libelle}" modifié avec succès`);
      }
      setModal(null);
    } catch (error) {
      toast.error(error.response?.data?.libelle?.[0] || "Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteModePaiement(slug, selected.id);
      setItems(prev => prev.filter(m => m.id !== selected.id));
      toast.success(`Mode de paiement "${selected.libelle}" supprimé avec succès`);
      setModal(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de supprimer ce mode de paiement — désactivez-le plutôt s'il est déjà utilisé");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2" style={{ justifyContent: "flex-end" }}>
        <button className="bsec" onClick={load}><i className="ti ti-refresh" />Actualiser</button>
        {/* caisse.configurer */}
        {peutConfigurer && (
          <button className="bprim" onClick={openCreate}><i className="ti ti-plus" />Nouveau mode de paiement</button>
        )}
      </div>

      <div className="card" style={TABLE_CARD}>
        <table className="tbl">
          <thead><tr><th>Libellé</th><th>Statut</th><th>Actions</th></tr></thead>
          <tbody>
            {items.map(m => (
              <tr key={m.id}>
                <td style={{ ...CELL_WRAP, fontWeight: 500 }}>{m.libelle}</td>
                <td><span className={`pill ${m.actif ? "ok" : "warn"}`}>{m.actif ? "Actif" : "Inactif"}</span></td>
                <td>
                  {/* caisse.configurer */}
                  {peutConfigurer && (
                    <div className="act">
                      <ActionBtn title="Modifier" icon="ti-edit" onClick={() => openEdit(m)} bg="#ffc107" color="black" />
                      <ActionBtn title="Supprimer" icon="ti-trash" onClick={() => openDelete(m)} bg="var(--rd)" />
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan="3" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucun mode de paiement trouvé</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(modal === "create" || modal === "edit") && (
        <Modal title={modal === "create" ? "Nouveau mode de paiement" : "Modifier le mode de paiement"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg">
              <label>Libellé *</label>
              <input type="text" placeholder="Ex: Espèces, Orange Money..." value={libelle} onChange={e => setLibelle(e.target.value)} />
            </div>
            {modal === "edit" && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={actif} onChange={e => setActif(e.target.checked)} />
                Actif
              </label>
            )}
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>{saving ? "..." : <><i className="ti ti-check" /> {modal === "create" ? "Créer" : "Enregistrer"}</>}</button>
          </div>
        </Modal>
      )}

      {modal === "delete" && selected && (
        <Modal title="Supprimer le mode de paiement" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>Supprimer <strong>{selected.libelle}</strong> ?</div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>{saving ? "..." : "Supprimer"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}